// src/pages/Dashboard/block/MenuHeader/MenuHeader.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog } from "../../../../layout/Dialog";
import { useAppShell } from "../../../../layout/AppShellContext";
import { useActiveCompanyId } from "../../../../api/useActiveCompanyId";
import "./menuHeader.scss";
import {
    api,
    setActiveCompanyId,
    getActiveCompanyId,
    setActiveCompanyIdApi,
    getActiveCompanyIdApi
} from "../../../../api/client";
import {
    fetchLkfrNotifications,
    LKFR_NOTIFICATIONS_UPDATED_EVENT,
} from "../../../../api/notificationInbox";

type CompanyDto = {
    id: number;
    idApi: number | null;
    address: string;
    title: string | null;
};

type ApiResponse = { items: any[] };

type MenuHeaderProps = { onMenuClick?: () => void; standalone?: boolean };

const MenuHeaderContent: React.FC<MenuHeaderProps> = ({ onMenuClick }) => {
    const shell = useAppShell();
    const { value: activeCompanyId } = useActiveCompanyId();
    const [search, setSearch] = useState("");
    const [companies, setCompanies] = useState<CompanyDto[]>([]);
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryRequest, setRetryRequest] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);

    const current = companies.find(c => c.id === currentId);
    useEffect(() => {
        if (activeCompanyId && companies.some(c => String(c.id) === activeCompanyId)) setCurrentId(Number(activeCompanyId));
    }, [activeCompanyId, companies]);

    useEffect(() => {
        if (!open) return;

        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [open]);

    useEffect(() => {
        let cancelled = false;

        const loadUnreadCount = async () => {
            try {
                const data = await fetchLkfrNotifications(1);
                if (!cancelled) {
                    setUnreadCount(Math.max(0, Number(data?.unreadCount ?? 0)));
                }
            } catch {
                if (!cancelled) {
                    setUnreadCount(0);
                }
            }
        };

        loadUnreadCount();

        const handleNotificationsUpdated = () => {
            loadUnreadCount();
        };

        window.addEventListener(LKFR_NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);

        return () => {
            cancelled = true;
            window.removeEventListener(LKFR_NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
        };
    }, []);

    const bellBadge = unreadCount > 99 ? "99+" : String(unreadCount);

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            setLoadFailed(false);
            try {
                const data = await api.get<ApiResponse>("/api/me/companies");
                const items: CompanyDto[] = (data?.items ?? [])
                    .map((i: any) => {
                        const id = parseNumericId(i.id);
                        const idApiRaw = parseNumericId(i.idApi);
                        const address = buildCompanyAddress(i, id, idApiRaw);

                        return {
                            id: id ?? Number.NaN,
                            idApi: idApiRaw,
                            address,
                            title: typeof i.title === "string"
                                ? i.title
                                : typeof i.legalName === "string"
                                    ? i.legalName
                                    : null,
                        };
                    })
                    .filter((i: CompanyDto) => Number.isFinite(i.id));

                if (aborted) return;

                setCompanies(items);

                const saved = getActiveCompanyId();
                const savedApi = getActiveCompanyIdApi();
                const savedNum = saved != null ? Number(saved) : null;
                const savedNumApi = savedApi != null ? Number(savedApi) : null;

                const savedItem = savedNum != null
                    ? items.find(i => i.id === savedNum)
                    : null;
                const savedApiItem = savedNumApi != null
                    ? items.find(i => i.idApi === savedNumApi)
                    : null;
                const nextItem = savedItem ?? savedApiItem ?? items[0] ?? null;

                const nextId = nextItem?.id ?? null;
                const nextIdApi = nextItem?.idApi ?? null;

                setCurrentId(nextId);

                setActiveCompanyId(nextId);
                setActiveCompanyIdApi(nextIdApi);
            } catch {
                if (!aborted) {
                    setLoadFailed(true);
                }
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => { aborted = true; };
    }, [retryRequest]);

    return (
        <>
            <div className="app-header">
                <button
                    className="header-menu"
                    aria-label="Открыть меню"
                    onClick={onMenuClick}
                >
                    <span /><span /><span />
                </button>

                <button
                    type="button"
                    className="header-logo-group"
                    onClick={() => companies.length && setOpen(true)}
                    aria-busy={loading}
                    disabled={!companies.length}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                >
                    <span className="header-address-label"><span>Текущий салон</span><span className="header-address-row"><span className="header-address" title={current?.address}>
                        {loading
                            ? "Загрузка адресов..."
                            : loadFailed
                                ? "Не удалось загрузить точки"
                                : current?.address ?? (companies.length === 0 ? "Нет доступных точек" : "Адрес не выбран")}
                    </span><svg className="header-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg></span></span>
                </button>

                {/* Колокольчик уведомлений */}
                <Link to="/notifications" className="header-bell" aria-label="Уведомления">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7"
                            stroke="#232137"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M13.73 21a2 2 0 01-3.46 0"
                            stroke="#232137"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    {unreadCount > 0 && <span className="header-bell-badge">{bellBadge}</span>}
                </Link>
            </div>

            {loadFailed && <div role="alert" className="header-load-error">
                Не удалось загрузить список точек.
                <button type="button" onClick={() => setRetryRequest((value) => value + 1)}>Повторить</button>
            </div>}

            {open && (
                <Dialog title="Выберите салон" onClose={() => setOpen(false)}>
                        <label className="lkfr-company-search">Поиск по адресу<input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Город или улица" /></label>
                        <ul className="address-modal-list">
                            {companies.filter(c => (c.address + " " + c.title).toLocaleLowerCase("ru").includes(search.toLocaleLowerCase("ru"))).map((c) => (
                                <li
                                    key={c.id}
                                    className={c.id === currentId ? "active" : ""}

                                >
                                    <button type="button" aria-pressed={c.id === currentId} onClick={() => {
                                        const select = () => { setCurrentId(c.id); setActiveCompanyId(c.id); setActiveCompanyIdApi(c.idApi); };
                                        setOpen(false); setSearch("");
                                        if (c.id !== currentId) { if (shell) shell.runGuarded(select); else select(); }
                                    }}><span>{c.address}</span>{c.id === currentId && <span className="address-check" aria-hidden="true">✓</span>}</button>
                                </li>
                            ))}
                        </ul>
                        <button className="address-modal-close" onClick={() => setOpen(false)}>Закрыть</button>
                </Dialog>
            )}
        </>
    );
};

function buildCompanyAddress(
    company: any,
    id?: number | null,
    idApi?: number | null
): string {
    const address = String(company?.address ?? "").trim();
    if (address.length > 0) {
        return address;
    }

    const city = String(company?.city ?? "").trim();
    const title = typeof company?.title === "string" ? company.title.trim() : "";
    const legalName = typeof company?.legalName === "string" ? company.legalName.trim() : "";
    const composed = [city, title || legalName].filter(Boolean).join(", ");
    if (composed.length > 0) {
        return composed;
    }

    if (typeof idApi === "number" && Number.isFinite(idApi)) {
        return `Точка #${idApi}`;
    }

    if (typeof id === "number" && Number.isFinite(id)) {
        return `Точка #${id}`;
    }

    return "Точка без адреса";
}

function parseNumericId(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

export const MenuHeader: React.FC<MenuHeaderProps> = (props) => {
    const shell = useAppShell();
    return shell && !props.standalone ? null : <MenuHeaderContent {...props} />;
};
